import { Box, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Stack } from '@mui/system';
import { useDebouncedValue } from '@mantine/hooks';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { bloxValues } from 'themes/constants';
import { hasLength } from 'utility/functions';
import { useRecentStoryTemplates } from 'components/editorial/AssetQuickLaunch/useRecentStoryTemplates';
import { PlusNavObj } from '../types';
import { templateToPlusNavObj } from '../templateNavItem';
import DnDBasePlusNavItem from './DnDBasePlusNavItem';

/**
 * WHY THIS SECTION FETCHES ITS OWN DATA:
 *
 * Templates are searched server-side, apps are just an in-memory list. Rather
 * than have PlusButton run the template query (and a new query per search type
 * we add later), each search-backed result group owns its own fetch. The single
 * top-bar query is routed in here as `query`; this section runs its own
 * useRecentStoryTemplates off it. The payoff: the create menu never becomes a
 * pile of per-type API calls, only the groups actually on screen fetch, and
 * adding a future searchable type means dropping in another self-contained
 * section, not editing a central orchestrator.
 *
 * Cost controls so this stays cheap as types grow:
 *  - permission-gated: this section only renders/fetches for editorial creators
 *  - min query length: the server search waits for 2+ chars (no call per keystroke)
 *  - debounced + react-query: repeated/abandoned queries are cached or cancelled
 */

// Don't hit the server until the query is meaningful; shorter inputs just
// client-filter the recent baseline that's already loaded.
const MIN_SERVER_QUERY_LENGTH = 2;

interface StoryTemplatesSectionProps {
  // The shared top search-bar query. Empty => browse recent templates.
  query: string;
  // Gate fetching to while the create menu is open (the popover stays mounted).
  isOpen: boolean;
  editMode: boolean;
  pinnedApps: string[];
  handleItemClick: (event: React.SyntheticEvent<HTMLElement>, item: PlusNavObj) => void;
  handlePinToggle: (item: PlusNavObj) => void;
}

const StoryTemplatesSection = ({
  query,
  isOpen,
  editMode,
  pinnedApps,
  handleItemClick,
  handlePinToggle,
}: StoryTemplatesSectionProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const trimmedQuery = query.trim();
  const untitledLabel = t(
    'blox.editorial.templates.createMenu.untitled',
    'Untitled Template',
  );

  // Debounce the server search so typing doesn't fire a request per keystroke.
  const [debouncedQuery] = useDebouncedValue(trimmedQuery, 300);
  const shouldServerSearch = debouncedQuery.length >= MIN_SERVER_QUERY_LENGTH;

  // Recent baseline (most recently created). Always loaded while open so the
  // section can browse and so we can client-filter it instantly as the user types.
  const {
    templates: baseline,
    isLoading: baselineLoading,
    isError: baselineError,
  } = useRecentStoryTemplates({ enabled: isOpen });

  // Server-side search. Only fires past the min length; react-query caches and
  // cancels so repeated/abandoned queries stay cheap.
  const { templates: searched } = useRecentStoryTemplates({
    enabled: isOpen && shouldServerSearch,
    searchQuery: debouncedQuery,
  });

  // Show an instant client-side filter of the recent baseline (so the list reacts
  // to every keystroke and clears in lockstep with the app/recommended results),
  // then fold in the server results.
  //
  // The server set is only merged once the debounce has caught up to what's typed
  // (`settled`). While the user is still typing, the live query is ahead of the
  // debounced one, so the server results are for an OLDER query, and react-query
  // keeps them around via placeholderData; showing them then would make templates
  // lag behind the rest of the menu (and a sub-min-length query would leak the
  // previous search's results). We deliberately do NOT filter the server results
  // by title: the backend `query` matches more than the title, so title-filtering
  // would silently drop legitimate matches.
  const templates = useMemo(() => {
    const live = trimmedQuery.toLowerCase();
    if (!live) return baseline;

    const clientFiltered = baseline.filter((tpl) =>
      tpl.title.toLowerCase().includes(live),
    );

    const settled = debouncedQuery === trimmedQuery;
    if (!settled || debouncedQuery.length < MIN_SERVER_QUERY_LENGTH) {
      return clientFiltered;
    }
    const merged = [...clientFiltered];
    for (const tpl of searched) {
      if (!merged.some((m) => m.uuid === tpl.uuid)) {
        merged.push(tpl);
      }
    }
    return merged;
  }, [baseline, searched, trimmedQuery, debouncedQuery]);

  // Only the baseline drives the section's loading/error state. The server search
  // is a background augmentation, so a slow or failed search must not blank out
  // the recent templates the user can already see and pin.
  const isLoading = baselineLoading;
  const isError = baselineError;

  // Build the nav items once per template-list change so the memo'd tiles keep
  // stable identities across unrelated re-renders (e.g. toggling edit mode).
  const items = useMemo(
    () => templates.map((tpl) => templateToPlusNavObj(tpl, untitledLabel)),
    [templates, untitledLabel],
  );

  const emptyStateMessage = isError
    ? t('blox.editorial.templates.createMenu.error', 'Unable to load story templates.')
    : t(
        'blox.editorial.templates.createMenu.empty',
        'No story templates yet. Create one to reuse its structure and metadata.',
      );

  // While searching, don't render a "no matches" placeholder for this group:
  // the app/recommended results stay silent when nothing matches, so templates
  // should too. Hiding the whole section (not just the message) avoids leaving a
  // lone "Story Templates" heading over an empty box. The first-run empty state
  // (no query, genuinely no templates) and the error state still show.
  if (!isLoading && !isError && trimmedQuery && !hasLength(templates)) {
    return null;
  }

  return (
    <Fragment>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography
          sx={{
            display: 'flex',
            alignItems: 'center',
            fontWeight: bloxValues.fontWeightMedium,
            fontSize: bloxValues.regularFontSizeRem,
            color: theme.palette.text.secondary,
          }}
        >
          {t('blox.editorial.templates.createMenu.heading', 'Story Templates')}
        </Typography>

        <Box
          data-testid="plus-button-templates-divider"
          id="plus-button-templates-divider"
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexGrow: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        />
      </Stack>

      {isLoading ? (
        <Box
          data-testid="plus-button-templates-loading"
          sx={{ display: 'flex', justifyContent: 'center', py: 2 }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : hasLength(templates) ? (
        <Box
          data-testid="plus-button-templates-grid"
          sx={{
            display: 'grid',
            // Match the Recommended grid above so the tiles share its footprint.
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: 1,
            justifyItems: 'center',
            alignItems: 'center',
          }}
        >
          {/* Same item component as Recommended/Pinned so templates share the
              icon, hover, and pin affordances. */}
          {items.map((item) => (
            <DnDBasePlusNavItem
              key={item.key}
              id={`plus-button-template-${item.template?.uuid}`}
              navItem={item}
              handleOnClick={(event) => handleItemClick(event, item)}
              asIcon={false}
              handlePinToggle={handlePinToggle}
              pinnedApps={pinnedApps}
              hidePin={!editMode}
              fullHeight={true}
              fullWidth={true}
            />
          ))}
        </Box>
      ) : (
        <Typography
          data-testid="plus-button-templates-empty"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: bloxValues.tinyFontSizeRem,
            border: `2px solid ${theme.palette.action.hover}`,
            backgroundColor: theme.palette.action.hover,
            p: 1,
            width: '100%',
            borderRadius: 1,
            textAlign: 'center',
          }}
        >
          {emptyStateMessage}
        </Typography>
      )}
    </Fragment>
  );
};

StoryTemplatesSection.displayName = 'StoryTemplatesSection';
export default StoryTemplatesSection;
